import { useEffect, useRef, useState } from 'react'
import { rememberFileMetadata } from 'components/UploadedFileList/fileMetadata'

interface Options {
  getUrls: () => string[]
  onChange: (urls: string[]) => void
  uploadFile: (file: File, extension: string) => Promise<string>
}
interface PendingFile {
  file: File
  status: 'uploading' | 'failed'
}

/** Reconcile each upload against its current local URL, preserving order and user removals. */
export function useMediaFileUploads(options: Options) {
  const [uploadingUrls, setUploadingUrls] = useState<Set<string>>(new Set())
  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set())

  const current = useRef(options)
  const pending = useRef(new Map<string, PendingFile>())
  const mounted = useRef(true)
  current.current = options

  const refresh = () => {
    if (!mounted.current) return
    setUploadingUrls(
      new Set(
        Array.from(pending.current)
          .filter(([, item]) => item.status === 'uploading')
          .map(([url]) => url)
      )
    )
    setFailedUrls(
      new Set(
        Array.from(pending.current)
          .filter(([, item]) => item.status === 'failed')
          .map(([url]) => url)
      )
    )
  }

  const start = async (localUrl: string, item: PendingFile) => {
    item.status = 'uploading'
    refresh()
    try {
      const uploadedUrl = await current.current.uploadFile(item.file, item.file.name.split('.').pop() || 'bin')
      if (!uploadedUrl) throw new Error('Upload returned no URL')
      if (!mounted.current || pending.current.get(localUrl) !== item) return
      const latest = current.current.getUrls()
      if (latest.includes(localUrl)) {
        rememberFileMetadata(uploadedUrl, item.file)
        current.current.onChange(latest.map((url) => (url === localUrl ? uploadedUrl : url)))
      }
      pending.current.delete(localUrl)
      URL.revokeObjectURL(localUrl)
    } catch {
      if (!mounted.current || pending.current.get(localUrl) !== item) return
      if (current.current.getUrls().includes(localUrl)) item.status = 'failed'
      else {
        pending.current.delete(localUrl)
        URL.revokeObjectURL(localUrl)
      }
    } finally {
      refresh()
    }
  }

  const add = (files: File[]) => {
    const additions = files.map((file) => {
      const url = URL.createObjectURL(file)
      const item: PendingFile = { file, status: 'uploading' }
      pending.current.set(url, item)
      rememberFileMetadata(url, file)
      return { url, item }
    })
    current.current.onChange([...current.current.getUrls(), ...additions.map(({ url }) => url)])
    additions.forEach(({ url, item }) => {
      void start(url, item)
    })
  }

  const remove = (url: string) => {
    if (pending.current.delete(url)) URL.revokeObjectURL(url)
    current.current.onChange(current.current.getUrls().filter((item) => item !== url))
    refresh()
  }

  const retry = (url: string) => {
    const item = pending.current.get(url)
    if (item?.status === 'failed' && current.current.getUrls().includes(url)) void start(url, item)
  }

  useEffect(() => {
    const files = pending.current
    return () => {
      mounted.current = false
      files.forEach((_, url) => URL.revokeObjectURL(url))
      files.clear()
    }
  }, [])

  return { add, remove, retry, uploadingUrls, failedUrls, isUploading: uploadingUrls.size > 0 }
}
